/* global Word, console */
import { useEffect, useCallback } from "react";
import Fuse from "fuse.js";

// Module-level variables for shared state (Singleton pattern)
let fuseInstance: Fuse<{ text: string; index: number }> | null = null;
let isDirty = true;
let isIndexing = false;

export const useFuzzySearch = () => {
    const buildIndex = useCallback(async () => {

        if (isIndexing) return;
        isIndexing = true;

        try {
            await Word.run(async (context) => {
                const paragraphs = context.document.body.paragraphs;
                context.load(paragraphs, "text");
                await context.sync();

                const paragraphTexts = paragraphs.items.map((p, index) => ({
                    text: p.text,
                    index: index,
                }));

                fuseInstance = new Fuse(paragraphTexts, {
                    keys: ["text"],
                    includeScore: true,
                    includeMatches: true,
                    threshold: 0.4,
                    ignoreLocation: true,
                });

                isDirty = false;
            });
        } catch (error) {
            console.error("Error building fuzzy search index:", error);
        } finally {
            isIndexing = false;
        }
    }, []);

    useEffect(() => {
        const setupListener = async () => {
            // Build index immediately on load if needed
            if (isDirty && !fuseInstance) {
                await buildIndex();
            }

            try {
                await Word.run(async (context) => {
                    context.document.onParagraphChanged.add(() => {
                        isDirty = true;
                        return Promise.resolve();
                    });
                    await context.sync();
                });
            } catch (error) {
                console.error("Failed to setup document change listener:", error);
            }
        };

        setupListener();
    }, [buildIndex]);

    const fuzzySearchInDocument = async (text: string): Promise<boolean> => {
        try {
            return await Word.run(async (context) => {
                if (isDirty || !fuseInstance) {
                    await buildIndex();
                }

                const results = fuseInstance?.search(text) || [];


                if (results.length > 0 && results[0]) {
                    const bestMatch = results[0];
                    const bestMatchIndex = bestMatch.item.index;

                    // We need to get the paragraph again to select it
                    const paragraphs = context.document.body.paragraphs;
                    context.load(paragraphs, "items");
                    await context.sync();

                    const bestMatchParagraph = paragraphs.items[bestMatchIndex];

                    if (bestMatchParagraph) {
                        // Try to find the specific matched text
                        if (bestMatch.matches && bestMatch.matches.length > 0) {
                            const firstMatch = bestMatch.matches[0];
                            if (firstMatch) {
                                const indices = firstMatch.indices;


                                // Find the longest contiguous match
                                let longestMatch = { start: 0, end: 0, length: 0 };

                                indices.forEach(([start, end]) => {
                                    const length = end - start + 1;
                                    if (length > longestMatch.length) {
                                        longestMatch = { start, end, length };
                                    }
                                });


                                // Only use the substring if it's substantial (e.g., > 3 chars)
                                if (longestMatch.length > 3) {
                                    const matchedText = bestMatch.item.text.substring(longestMatch.start, longestMatch.end + 1);


                                    // Search for this specific substring within the paragraph
                                    const searchResults = bestMatchParagraph.search(matchedText, {
                                        ignorePunct: true,
                                        ignoreSpace: true,
                                    });
                                    context.load(searchResults, "items");
                                    await context.sync();

                                    if (searchResults.items.length > 0) {
                                        searchResults.items[0]?.select();
                                        await context.sync();
                                        return true;
                                    }
                                }
                            }
                        }

                        // Fallback to selecting the whole paragraph if substring search fails or is too short
                        bestMatchParagraph.select();
                        await context.sync();
                        return true;
                    }
                }

                return false;
            });
        } catch (error) {
            console.error("Error in fuzzySearchInDocument:", error);
            return false;
        }
    };

    return { fuzzySearchInDocument, buildIndex };
};
